import { useState } from 'react'
import { Download, X, CheckCircle, FileText, Route, ArrowDownCircle, Smartphone, Bot, Copy, Check } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import toast from 'react-hot-toast'

export default function IntegrationsPage() {
  const [showInstructions, setShowInstructions] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownload = async () => {
    setIsDownloading(true)
    try {
      // Trigger download
      const response = await fetch('/api/extension/download')
      if (!response.ok) throw new Error('Download failed')
      
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'memtrail-extension.zip'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      
      // Show instructions after download starts
      setTimeout(() => {
        setShowInstructions(true)
        setIsDownloading(false)
      }, 500)
    } catch (error) {
      console.error('Download error:', error)
      alert('Failed to download extension. Please try again.')
      setIsDownloading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">Connect</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Connect Memtrail with your favorite tools and workflows
          </p>
        </div>

        {/* Browser Extensions Section */}
        <div className="mb-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-5">Browser Extensions</h2>
            <div className="flex items-center gap-8 flex-wrap">
              {/* Chrome */}
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <img src="https://cdn.simpleicons.org/googlechrome" alt="Chrome" className="w-full h-full" />
                  </div>
                  <a href="https://chromewebstore.google.com/detail/memtrail-%E2%80%94-your-personal/kacdkdlbkoleandfhipclafnkmbkneoa" target="_blank" rel="noopener noreferrer"
                    className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center shadow-lg transition-all">
                    <ArrowDownCircle className="w-3.5 h-3.5 text-white" />
                  </a>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Chrome</p>
                  <p className="text-xs text-green-600 font-medium">Available</p>
                </div>
              </div>
              {/* Firefox */}
              <div className="flex items-center gap-3 opacity-40">
                <div className="w-12 h-12 flex items-center justify-center">
                  <img src="https://cdn.simpleicons.org/firefox" alt="Firefox" className="w-full h-full" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Firefox</p>
                  <p className="text-xs text-gray-500">Coming soon</p>
                </div>
              </div>
              {/* Brave */}
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <img src="https://cdn.simpleicons.org/brave" alt="Brave" className="w-full h-full" />
                  </div>
                  <a href="https://chromewebstore.google.com/detail/memtrail-%E2%80%94-your-personal/kacdkdlbkoleandfhipclafnkmbkneoa" target="_blank" rel="noopener noreferrer"
                    className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center shadow-lg transition-all">
                    <ArrowDownCircle className="w-3.5 h-3.5 text-white" />
                  </a>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Brave</p>
                  <p className="text-xs text-green-600 font-medium">Available</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile App Section */}
        <div className="mb-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-5">Mobile App</h2>
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl flex-shrink-0 bg-gray-50 dark:bg-gray-700 border border-gray-100 dark:border-gray-600 flex items-center justify-center">
                <img src="/smartphone-call.png" alt="Mobile App" className="w-9 h-9 object-contain" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Memtrail for Mobile</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 leading-relaxed max-w-sm">
                  Your knowledge library in your pocket. Save links, capture ideas, and chat with your AI assistant anywhere.
                </p>
                <div className="flex gap-3 flex-wrap">
                  <div className="relative">
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-black rounded-xl opacity-40 cursor-not-allowed select-none">
                      <img src="https://cdn.simpleicons.org/apple/ffffff" alt="Apple" className="w-5 h-5" />
                      <div className="text-left">
                        <div className="text-[9px] text-gray-300 leading-none">Download on the</div>
                        <div className="text-sm font-semibold text-white leading-tight">App Store</div>
                      </div>
                    </div>
                    <span className="absolute -top-2 -right-2 bg-gray-800 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-full leading-none tracking-wide">SOON</span>
                  </div>
                  <div className="relative">
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-black rounded-xl opacity-40 cursor-not-allowed select-none">
                      <img src="https://cdn.simpleicons.org/googleplay/ffffff" alt="Google Play" className="w-5 h-5" />
                      <div className="text-left">
                        <div className="text-[9px] text-gray-300 leading-none">Get it on</div>
                        <div className="text-sm font-semibold text-white leading-tight">Google Play</div>
                      </div>
                    </div>
                    <span className="absolute -top-2 -right-2 bg-gray-800 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-full leading-none tracking-wide">SOON</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Export & Tools Section */}
        <div className="mb-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-5">Export & Tools</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <ObsidianExportCard />
              <NotionExportCard />
            </div>
          </div>
        </div>

        {/* AI & Agents Section */}
        <div className="mb-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6">
            <div className="mb-5">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">AI & Agents</h2>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Connect your knowledge base to AI tools and autonomous agents</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <McpServerCard />
            </div>
          </div>
        </div>
      </div>

      {/* Installation Instructions Modal */}
      {showInstructions && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
              {/* Header */}
              <div className="relative p-6 border-b border-gray-100">
                <button
                  onClick={() => setShowInstructions(false)}
                  className="absolute top-4 right-4 p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
                
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">Extension Downloaded</h2>
                </div>
                <p className="text-xs text-gray-500 ml-11">
                  Works with Chrome, Brave, and other Chromium browsers
                </p>
              </div>

              {/* Instructions */}
              <div className="p-6 space-y-5">
                {/* Step 1 */}
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                    1
                  </div>
                  <div className="flex-1 pt-0.5">
                    <h3 className="text-sm font-medium text-gray-900 mb-1">Extract the ZIP file</h3>
                    <p className="text-xs text-gray-600">
                      Right-click <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">memtrail-extension.zip</code> and extract
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                    2
                  </div>
                  <div className="flex-1 pt-0.5">
                    <h3 className="text-sm font-medium text-gray-900 mb-1">Open Extensions page</h3>
                    <div className="space-y-1">
                      <code className="block px-2 py-1 bg-gray-100 rounded text-xs">chrome://extensions</code>
                      <code className="block px-2 py-1 bg-gray-100 rounded text-xs">brave://extensions</code>
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                    3
                  </div>
                  <div className="flex-1 pt-0.5">
                    <h3 className="text-sm font-medium text-gray-900 mb-1">Enable Developer mode</h3>
                    <p className="text-xs text-gray-600">
                      Toggle the switch in the top-right corner
                    </p>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                    4
                  </div>
                  <div className="flex-1 pt-0.5">
                    <h3 className="text-sm font-medium text-gray-900 mb-1">Load unpacked extension</h3>
                    <p className="text-xs text-gray-600">
                      Click "Load unpacked" and select the extracted folder
                    </p>
                  </div>
                </div>

                {/* Success */}
                <div className="flex gap-3 pt-2">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <div className="flex-1 pt-0.5">
                    <p className="text-xs text-gray-600">
                      You're all set! Visit any webpage and click the Memtrail icon.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  )
}

// Obsidian Export Card Component
function ObsidianExportCard() {
  const { token } = useAuthStore()
  const [isExporting, setIsExporting] = useState(false)
  const [stats, setStats] = useState<{ articles: number; trails: number } | null>(null)
  const [lastExport, setLastExport] = useState<string | null>(null)

  // Fetch stats on mount
  useState(() => {
    const fetchStats = async () => {
      if (!token) return
      try {
        const response = await fetch('/api/export/stats', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response.ok) {
          const data = await response.json()
          setStats(data.stats)
          setLastExport(data.lastExport)
        }
      } catch (error) {
        console.error('Failed to fetch export stats:', error)
      }
    }
    fetchStats()
  })

  const handleExport = async () => {
    if (!token) {
      toast.error('Please log in to export')
      return
    }

    setIsExporting(true)

    try {
      const response = await fetch('/api/export/obsidian', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Export failed')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const date = new Date().toISOString().split('T')[0]
      a.download = `Memtrail-vault-${date}.zip`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      setLastExport(new Date().toISOString())
      // Silent success - file downloads automatically
    } catch (error) {
      console.error('Export error:', error)
      toast.error('Failed to export. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
      <div className="flex items-start gap-4">
        {/* Obsidian Icon */}
        <div className="flex-shrink-0">
          <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            <img 
              src="https://cdn.simpleicons.org/obsidian/7C3AED" 
              alt="Obsidian" 
              className="w-6 h-6"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Obsidian</h3>
            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs font-medium rounded">
              Markdown
            </span>
          </div>
          
          <p className="text-gray-600 dark:text-gray-400 text-xs mb-3">
            Export as markdown with backlinks and tags. Open in Obsidian as a vault.
          </p>

          {/* Stats Grid */}
          {stats && (
            <div className="flex items-center gap-4 mb-3">
              <div className="flex items-center gap-2">
                <FileText className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-bold text-gray-900 dark:text-white">{stats.articles}</span> Articles
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <Route className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-bold text-gray-900 dark:text-white">{stats.trails}</span> Trails
                </span>
              </div>
            </div>
          )}

          {/* Export Button */}
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-3"
          >
            <Download className="w-3.5 h-3.5" />
            {isExporting ? 'Exporting...' : 'Export as Markdown'}
          </button>

          {/* How to Use - Collapsible */}
          <details className="group">
            <summary className="text-xs font-medium text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors list-none flex items-center gap-1.5">
              <span className="text-gray-400 group-open:rotate-90 transition-transform">▶</span>
              How to use in Obsidian
            </summary>
            <div className="mt-2 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
              <ol className="text-xs text-gray-600 dark:text-gray-400 space-y-1.5 list-decimal list-inside">
                <li>Extract the downloaded ZIP file</li>
                <li>Open Obsidian → Click "Open folder as vault"</li>
                <li>Select the extracted folder</li>
                <li>Your content appears with [[wikilinks]] and tags</li>
              </ol>
            </div>
          </details>
        </div>
      </div>
    </div>
  )
}

// Notion Export Card Component
function NotionExportCard() {
  const { token } = useAuthStore()
  const [isExporting, setIsExporting] = useState(false)

  const handleExport = async () => {
    if (!token) {
      toast.error('Please log in to export')
      return
    }
    setIsExporting(true)
    try {
      // Reuse the obsidian export endpoint — markdown is Notion-compatible
      const response = await fetch('/api/export/obsidian', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      if (!response.ok) throw new Error('Export failed')
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const date = new Date().toISOString().split('T')[0]
      a.download = `Memtrail-notion-${date}.zip`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      toast.error('Failed to export. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            <img
              src="https://cdn.simpleicons.org/notion/000000"
              alt="Notion"
              className="w-6 h-6 dark:invert"
            />
          </div>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Notion</h3>
            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs font-medium rounded">
              Markdown
            </span>
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-xs mb-3">
            Export your library as markdown files. Import directly into Notion as pages.
          </p>
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-3"
          >
            <Download className="w-3.5 h-3.5" />
            {isExporting ? 'Exporting...' : 'Export for Notion'}
          </button>
          <details className="group">
            <summary className="text-xs font-medium text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors list-none flex items-center gap-1.5">
              <span className="text-gray-400 group-open:rotate-90 transition-transform">▶</span>
              How to import into Notion
            </summary>
            <div className="mt-2 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
              <ol className="text-xs text-gray-600 dark:text-gray-400 space-y-1.5 list-decimal list-inside">
                <li>Extract the downloaded ZIP file</li>
                <li>In Notion, click "Import" → "Markdown & CSV"</li>
                <li>Select the extracted folder or individual .md files</li>
                <li>Your content appears as Notion pages</li>
              </ol>
            </div>
          </details>
        </div>
      </div>
    </div>
  )
}

// MCP Server Card Component
function McpServerCard() {
  const { token } = useAuthStore()
  const [copied, setCopied] = useState(false)

  const mcpConfig = `{
  "mcpServers": {
    "memtrail": {
      "command": "npx",
      "args": ["-y", "@memtrail/mcp-server"],
      "env": {
        "MEMTRAIL_API_KEY": "${token?.slice(0, 8) ?? 'your-api-key'}..."
      }
    }
  }
}`

  const handleCopy = () => {
    navigator.clipboard.writeText(mcpConfig)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center p-1">
            {/* Official MCP logo from modelcontextprotocol.io */}
            <img
              src="https://avatars.githubusercontent.com/u/182288589?s=200&v=4"
              alt="MCP"
              className="w-full h-full rounded-md"
            />
          </div>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">MCP Server</h3>
            <span className="px-2 py-0.5 bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-xs font-medium rounded">
              Coming soon
            </span>
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-xs mb-3 leading-relaxed">
            Expose your Memtrail knowledge base as an MCP server. Let Claude, Cursor, Kiro, and any AI agent search and read your saved content directly — no copy-paste, no context switching.
          </p>
          <div className="relative mb-3">
            <pre className="text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 overflow-x-auto text-gray-600 dark:text-gray-400 select-all">
              {mcpConfig}
            </pre>
            <button
              onClick={handleCopy}
              className="absolute top-2 right-2 p-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              title="Copy config"
            >
              {copied
                ? <Check className="w-3.5 h-3.5 text-green-500" />
                : <Copy className="w-3.5 h-3.5 text-gray-400" />
              }
            </button>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Works with Claude Desktop, Cursor, Kiro, and any MCP-compatible AI tool.
          </p>
        </div>
      </div>
    </div>
  )
}

